import React, { useEffect, useState } from "react";
import { Box, Flex, Badge } from "theme-ui";
import { getPipelineFunnel, PipelineFunnelData } from "api/Dashboard";
import { themevals } from "theme/themevals";

const CHART_MIN_HEIGHT = 300;

type TimeFilter = "month" | "quarter" | "year" | "all";

const formatCurrencyShort = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}k`;
  }
  return `$${value}`;
};

// Some stages (RFQs) carry a value RANGE rather than a single figure, because
// the underlying RFQs price alternative service options the client picks one
// of. Show "low - high" when a distinct range is present, else a single value.
const formatStageValue = (stage: {
  value: number;
  valueMin?: number;
  valueMax?: number;
}): string => {
  const hasRange =
    stage.valueMin != null &&
    stage.valueMax != null &&
    stage.valueMax > stage.valueMin;
  if (hasRange) {
    return `${formatCurrencyShort(stage.valueMin as number)} - ${formatCurrencyShort(
      stage.valueMax as number,
    )}`;
  }
  return formatCurrencyShort(stage.value);
};

const timeFilterLabels: Record<TimeFilter, string> = {
  month: "This Month",
  quarter: "This Quarter",
  year: "This Year",
  all: "All Time",
};

const timeFilterOrder: TimeFilter[] = ["month", "quarter", "year", "all"];

const PipelineFunnel: React.FC = () => {
  const [funnelData, setFunnelData] = useState<PipelineFunnelData | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("month");
  // Cache results per timeframe so re-selecting a visited timeframe is instant
  // and does not refire the (heavy) funnel query. Switching still fetches once
  // per timeframe, but the previous data stays on screen while it loads instead
  // of flashing blank.
  const [cache, setCache] = useState<Partial<Record<TimeFilter, PipelineFunnelData>>>({});

  useEffect(() => {
    let cancelled = false;

    // Show cached data for this timeframe immediately if we have it.
    if (cache[timeFilter]) {
      setFunnelData(cache[timeFilter] as PipelineFunnelData);
      setInitialLoading(false);
      return;
    }

    const fetchData = async () => {
      const data = await getPipelineFunnel(timeFilter);
      if (cancelled) return;
      setFunnelData(data);
      setCache((prev) => ({ ...prev, [timeFilter]: data }));
      setInitialLoading(false);
    };
    fetchData();

    return () => {
      cancelled = true;
    };
  }, [timeFilter, cache]);

  // Calculate bar width as percentage of the max count across all stages
  const getBarWidth = (index: number): number => {
    if (!funnelData || funnelData.stages.length === 0) return 100;
    
    const maxCount = Math.max(...funnelData.stages.map(s => s.count), 1);
    const stageCount = funnelData.stages[index]?.count || 0;
    
    // Calculate percentage, with a minimum of 15% so bars don't disappear
    const percentage = (stageCount / maxCount) * 100;
    return Math.max(percentage, 15);
  };

  const renderTimeFilters = () => (
    <Flex sx={{ gap: "8px" }}>
      {timeFilterOrder.map((filter) => (
        <Badge
          key={filter}
          variant={timeFilter === filter ? "grey" : "ltgrey"}
          sx={{
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              opacity: timeFilter === filter ? 1 : 0.8,
            },
          }}
          onClick={() => setTimeFilter(filter)}
        >
          {timeFilterLabels[filter]}
        </Badge>
      ))}
    </Flex>
  );

  if (initialLoading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 4px 12px 0px rgba(113, 125, 150, 0.05)",
          flex: "1 1 100%",
          "@media screen and (min-width: 2001px)": {
            flex: "1 1 45%",
          },
          minHeight: `${CHART_MIN_HEIGHT}px`,
        }}
      >
        <Flex
          sx={{
            padding: "16px 16px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "76px",
          }}
        >
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "base_800",
            }}
          >
            Pipeline Funnel
          </Box>
          {renderTimeFilters()}
        </Flex>
        <Box sx={{ padding: "20px" }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "200px", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  if (!funnelData || funnelData.stages.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 4px 12px 0px rgba(113, 125, 150, 0.05)",
          flex: "1 1 100%",
          "@media screen and (min-width: 2001px)": {
            flex: "1 1 45%",
          },
          minHeight: `${CHART_MIN_HEIGHT}px`,
        }}
      >
        <Flex
          sx={{
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "76px"
          }}
        >
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "base_800",
            }}
          >
            Pipeline Funnel
          </Box>
          {renderTimeFilters()}
        </Flex>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: "base_500",
            fontFamily: "Poppins, sans-serif",
          }}
        >
          No pipeline data available
        </Box>
      </Box>
    );
  }

  const barColor = themevals.colors.info;
  const lightBlue = "#E8F0F8";

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: "1 1 100%",
        "@media screen and (min-width: 2001px)": {
          flex: "1 1 45%",
        },
        minHeight: `${CHART_MIN_HEIGHT}px`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Flex
        sx={{
          padding: "16px 20px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "76px"
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          Pipeline Funnel
        </Box>
        {renderTimeFilters()}
      </Flex>

      <Box sx={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Flex sx={{ flexDirection: "column", gap: "10px" }}>
          {funnelData.stages.map((stage, index) => (
            <Box key={stage.stage} sx={{ position: "relative" }}>
              {/* Light blue background extending full width */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: lightBlue,
                  borderRadius: "6px",
                }}
              />

              {/* Dark blue bar with dynamic width based on max count ratio.
                  Purely the visual fill - the text is rendered separately in a
                  full-width overlay so it is never clipped by a narrow bar. */}
              <Box
                sx={{
                  position: "relative",
                  width: `${getBarWidth(index)}%`,
                  backgroundColor: barColor,
                  borderRadius: "6px",
                  minHeight: "40px",
                  transition: "width 0.4s ease-out",
                }}
              />

              {/* Text overlay: label + count/value grouped on the LEFT so both
                  sit over the dark bar fill (bars are min 15% wide). Full-width
                  overlay means nothing is clipped by a narrow bar, and keeping
                  the text left-aligned avoids the far-right value landing on the
                  pale track where white would be unreadable. */}
              <Flex
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: "10px 14px",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "12px",
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#FFFFFF",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stage.label}
                </Box>
                <Box
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.9)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stage.count} • {formatStageValue(stage)}
                </Box>
              </Flex>
            </Box>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

export default PipelineFunnel;
